import {
  YES_NO,
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * B2. Zgłoszenie eksploatacji przydomowej oczyszczalni ścieków (art. 152 POŚ, § 2 ust. 1 rozporządzenia MŚ
 * z 2.07.2010 r. — oczyszczalnie do 5 m³/d na potrzeby gospodarstwa domowego lub rolnego) albo zgłoszenie zbiornika
 * bezodpływowego lub przydomowej oczyszczalni do gminnej ewidencji (art. 3 ust. 3 pkt 1–2 u.c.p.g.). Dwa formularze
 * z katalogu jako warianty. Brak wzorów ogólnopolskich; układ jak wzory urzędów pobrane 2026-10-22:
 * - zgłoszenie eksploatacji — Gmina Sulechów (https://www.bip.sulechow.pl/res/serwisy/pliki/32401684?version=1.0,
 *   pkt 1–11 = treść art. 152 ust. 2),
 * - ewidencja — Miasto Szczecinek (https://bip.szczecinek.pl/attachments/download/9452: dane nieruchomości, zbiornik,
 *   oczyszczalnia, umowa na opróżnianie).
 * Mapa, certyfikat oczyszczalni i kopia zgłoszenia budowy to praktyka urzędów (nieobowiązkowe) — do potwierdzenia
 * z wydziałem. Zgody RODO z wzorów pominięte (EBI ma własną klauzulę).
 */
const OPERATION: EbiCondition = { field: "rodzajZgloszenia", equals: "eksploatacja" };
const REGISTER: EbiCondition = { field: "rodzajZgloszenia", equals: "ewidencja" };
const TANK: EbiCondition = { all: [REGISTER, { field: "rodzajUrzadzenia", equals: "zbiornik" }] };
const PLANT: EbiCondition = { all: [REGISTER, { field: "rodzajUrzadzenia", equals: "oczyszczalnia" }] };
const FEE_WAIVED: EbiCondition = { field: "oplataZgloszenia", equals: "zwolnienie" };
const EMPTYING_CONTRACT: EbiCondition = { all: [REGISTER, { field: "umowaWywoz", equals: "tak" }] };

const B2: IEbiFormDefinition = {
  code: "B2",
  version: 1,
  title: "Zgłoszenie przydomowej oczyszczalni ścieków albo zbiornika bezodpływowego",
  category: EEbiFormCategory.Environment,
  office: EEbiFormOffice.Municipality,
  documentType: "Pismo",
  description:
    "Zgłoszenie eksploatacji przydomowej oczyszczalni ścieków (do 5 m³/d) albo zgłoszenie szamba lub oczyszczalni do gminnej ewidencji.",
  keywords: ["oczyszczalnia", "przydomowa oczyszczalnia", "szambo", "zbiornik bezodpływowy", "ewidencja", "ścieki", "nieczystości ciekłe"],
  organ: "Wójt / Burmistrz / Prezydent Miasta (organ ochrony środowiska; prowadzi ewidencję zbiorników bezodpływowych i przydomowych oczyszczalni)",
  legalBasis:
    "art. 152 ustawy z dnia 27 kwietnia 2001 r. — Prawo ochrony środowiska; § 2 ust. 1 rozporządzenia Ministra Środowiska z dnia 2 lipca 2010 r. w sprawie rodzajów instalacji, których eksploatacja wymaga zgłoszenia; art. 3 ust. 3 pkt 1–2 ustawy z dnia 13 września 1996 r. o utrzymaniu czystości i porządku w gminach",
  request:
    "Zgłaszam przydomową oczyszczalnię ścieków do eksploatacji (art. 152 ustawy — Prawo ochrony środowiska) albo zbiornik bezodpływowy lub przydomową oczyszczalnię ścieków do gminnej ewidencji — zgodnie z rodzajem zgłoszenia wskazanym poniżej.",
  feeLabel: "do 120 zł",
  fee: "Zgłoszenie eksploatacji oczyszczalni — 120 zł opłaty skarbowej (zwolnienie, gdy oczyszczalnia obsługuje wyłącznie budynek mieszkalny); zgłoszenie do ewidencji — bez opłaty; pełnomocnictwo — 17 zł.",
  deadlineLabel: "30 dni na sprzeciw",
  deadline:
    "Zgłoszenie eksploatacji — eksploatację można rozpocząć, jeśli organ w ciągu 30 dni od doręczenia zgłoszenia nie wniesie sprzeciwu w drodze decyzji (art. 152 ust. 3–4 POŚ). Wpis do ewidencji — bez decyzji.",
  variantField: "rodzajZgloszenia",
  variants: {
    eksploatacja: { title: "Zgłoszenie eksploatacji przydomowej oczyszczalni ścieków", documentType: "Pismo" },
    ewidencja: { title: "Zgłoszenie do ewidencji zbiorników bezodpływowych i przydomowych oczyszczalni ścieków", documentType: "Pismo" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj zgłoszenia",
      fields: [
        {
          code: "rodzajZgloszenia",
          type: "radio",
          label: "Zgłaszam",
          required: true,
          options: [
            {
              value: "eksploatacja",
              label:
                "eksploatację przydomowej oczyszczalni ścieków o przepustowości do 5 m³ na dobę, wykorzystywanej na potrzeby gospodarstwa domowego lub rolnego (art. 152 POŚ) — przed rozpoczęciem eksploatacji",
            },
            {
              value: "ewidencja",
              label: "zbiornik bezodpływowy (szambo) albo przydomową oczyszczalnię ścieków do gminnej ewidencji (art. 3 ust. 3 u.c.p.g.)",
            },
          ],
        },
      ],
    },
    applicantSection("Zgłaszający (prowadzący instalację / właściciel nieruchomości)"),
    representationSection(),
    {
      code: "nieruchomosc",
      title: "Nieruchomość",
      fields: [
        {
          code: "tytulWladania",
          type: "radio",
          label: "Zgłaszający jest",
          required: true,
          visibleWhen: REGISTER,
          options: [
            { value: "wlasciciel", label: "właścicielem lub współwłaścicielem nieruchomości" },
            { value: "uzytkownik", label: "użytkownikiem wieczystym albo użytkownikiem nieruchomości" },
            { value: "inny", label: "inną osobą władającą nieruchomością" },
          ],
        },
        { code: "adresNieruchomosci", type: "text", label: "Adres nieruchomości (ulica, numer, miejscowość)", required: true, maxLength: 300 },
        { code: "obreb", type: "text", label: "Obręb ewidencyjny", required: true, width: "half", maxLength: 100 },
        { code: "dzialka", type: "text", label: "Numer działki ewidencyjnej", required: true, width: "half", maxLength: 100, placeholder: "np. 125/4" },
        { code: "liczbaOsob", type: "number", label: "Liczba osób zamieszkujących nieruchomość", required: true, min: 0, max: 999, width: "half" },
        {
          code: "okresKorzystania",
          type: "text",
          label: "Okres korzystania z nieruchomości",
          visibleWhen: REGISTER,
          width: "half",
          maxLength: 100,
          placeholder: "np. cały rok, sezonowo od maja do września",
        },
        { code: "wlasneUjecie", type: "radio", label: "Czy nieruchomość ma własne ujęcie wody?", required: true, visibleWhen: REGISTER, options: YES_NO },
        {
          code: "zgloszenieBudowy",
          type: "radio",
          label: "Czy budowa zbiornika lub oczyszczalni została zgłoszona staroście (Prawo budowlane)?",
          options: YES_NO,
        },
      ],
    },
    {
      code: "eksploatacja",
      title: "Przydomowa oczyszczalnia ścieków — dane z art. 152 ust. 2 POŚ",
      visibleWhen: OPERATION,
      fields: [
        {
          code: "przeznaczenie",
          type: "radio",
          label: "Przeznaczenie oczyszczalni",
          required: true,
          options: [
            { value: "domowe", label: "na potrzeby gospodarstwa domowego" },
            { value: "rolne", label: "na potrzeby gospodarstwa rolnego" },
          ],
        },
        {
          code: "typOczyszczalni",
          type: "radio",
          label: "Typ przydomowej oczyszczalni ścieków",
          required: true,
          options: [
            { value: "drenazowa", label: "drenażowo-rozsączająca" },
            { value: "roslinna", label: "roślinna" },
            { value: "biologiczna", label: "biologiczno-mechaniczna" },
            { value: "inna", label: "inna" },
          ],
        },
        { code: "typOczyszczalniInny", type: "text", label: "Typ oczyszczalni (inna)", required: true, visibleWhen: { field: "typOczyszczalni", equals: "inna" }, maxLength: 200 },
        {
          code: "przepustowosc",
          type: "number",
          label: "Przepustowość oczyszczalni [m³/doba]",
          required: true,
          decimals: 2,
          min: 0,
          max: 5,
          width: "half",
          hint: "Zgłoszenie dotyczy oczyszczalni o przepustowości do 5 m³ na dobę.",
        },
        { code: "pojemnosc", type: "number", label: "Pojemność oczyszczalni [m³]", decimals: 2, min: 0, max: 1000, width: "half" },
        {
          code: "iloscSciekow",
          type: "number",
          label: "Ilość wprowadzanych ścieków bytowych [m³/rok]",
          required: true,
          decimals: 2,
          min: 0,
          max: 100000,
          width: "half",
        },
        { code: "terminRozpoczecia", type: "date", label: "Planowany termin rozpoczęcia eksploatacji", width: "half" },
        {
          code: "czasFunkcjonowania",
          type: "radio",
          label: "Czas funkcjonowania oczyszczalni",
          required: true,
          defaultValue: "caloroczny",
          options: [
            { value: "caloroczny", label: "praca przez cały rok, całą dobę" },
            { value: "okresowy", label: "praca okresowa" },
          ],
        },
        {
          code: "okresFunkcjonowania",
          type: "text",
          label: "Okres pracy (dni tygodnia, godziny, miesiące)",
          required: true,
          visibleWhen: { field: "czasFunkcjonowania", equals: "okresowy" },
          maxLength: 200,
        },
        {
          code: "metodyOgraniczania",
          type: "textarea",
          label: "Opis stosowanych metod ograniczania wielkości emisji",
          required: true,
          maxLength: 3000,
          hint: "Np. stopień redukcji zanieczyszczeń BZT₅ i zawiesiny ogólnej [%], sposób postępowania z nadmiernym osadem.",
        },
        {
          code: "zgodnoscZPrzepisami",
          type: "textarea",
          label: "Informacja, czy stopień ograniczania wielkości emisji jest zgodny z obowiązującymi przepisami",
          required: true,
          maxLength: 1000,
          hint: "Np. czy oczyszczalnia ma certyfikat lub deklarację właściwości użytkowych i jaki (norma).",
        },
        feeInfo(
          "infoSprzeciw",
          "Eksploatację oczyszczalni możesz rozpocząć, jeżeli w ciągu 30 dni od doręczenia zgłoszenia organ nie wniesie sprzeciwu w drodze decyzji. O zmianie danych zgłoszenia albo zakończeniu eksploatacji poinformuj organ (art. 152 ustawy — Prawo ochrony środowiska).",
        ),
      ],
    },
    {
      code: "urzadzenie",
      title: "Sposób gromadzenia nieczystości ciekłych",
      visibleWhen: REGISTER,
      fields: [
        {
          code: "rodzajUrzadzenia",
          type: "radio",
          label: "Na nieruchomości jest",
          required: true,
          options: [
            { value: "zbiornik", label: "zbiornik bezodpływowy (szambo)" },
            { value: "oczyszczalnia", label: "przydomowa oczyszczalnia ścieków" },
          ],
        },
        {
          code: "zbKonstrukcja",
          type: "radio",
          label: "Konstrukcja zbiornika",
          required: true,
          visibleWhen: TANK,
          options: [
            { value: "jednokomorowy", label: "zbiornik jednokomorowy" },
            { value: "dwukomorowy", label: "zbiornik dwukomorowy" },
            { value: "trzykomorowy", label: "zbiornik trzykomorowy" },
          ],
        },
        { code: "zbLiczba", type: "number", label: "Liczba zbiorników", required: true, visibleWhen: TANK, min: 1, max: 20, width: "third" },
        { code: "zbPojemnosc", type: "number", label: "Pojemność zbiorników [m³]", required: true, visibleWhen: TANK, decimals: 2, min: 0, max: 1000, width: "third" },
        { code: "zbRokBudowy", type: "number", label: "Rok budowy", visibleWhen: TANK, min: 1900, max: 2100, width: "third" },
        {
          code: "zbMaterial",
          type: "radio",
          label: "Technologia wykonania zbiornika (materiał)",
          required: true,
          visibleWhen: TANK,
          options: [
            { value: "kregi", label: "kręgi betonowe" },
            { value: "zalewany", label: "zalewany betonem" },
            { value: "metalowy", label: "metalowy" },
            { value: "poliestrowy", label: "poliestrowy" },
            { value: "inny", label: "inny" },
          ],
        },
        {
          code: "zbMaterialInny",
          type: "text",
          label: "Materiał zbiornika (inny)",
          required: true,
          visibleWhen: { all: [TANK, { field: "zbMaterial", equals: "inny" }] },
          maxLength: 100,
        },
        {
          code: "posTyp",
          type: "radio",
          label: "Typ przydomowej oczyszczalni",
          required: true,
          visibleWhen: PLANT,
          options: [
            { value: "biologicznaDrenaz", label: "mechaniczno-biologiczna z drenażem rozsączającym" },
            { value: "biologicznaWody", label: "mechaniczno-biologiczna z odprowadzaniem do wód" },
            { value: "mechanicznaDrenaz", label: "mechaniczna (odstojnik) z drenażem rozsączającym" },
            { value: "mechanicznaWody", label: "mechaniczna (odstojnik) z odprowadzaniem do wód" },
            { value: "inna", label: "inna" },
          ],
        },
        {
          code: "posTypInny",
          type: "text",
          label: "Typ oczyszczalni (inna)",
          required: true,
          visibleWhen: { all: [PLANT, { field: "posTyp", equals: "inna" }] },
          maxLength: 200,
        },
        { code: "posProducent", type: "text", label: "Producent i model oczyszczalni", visibleWhen: PLANT, maxLength: 200 },
        { code: "posPrzepustowosc", type: "number", label: "Przepustowość oczyszczalni [m³/d]", required: true, visibleWhen: PLANT, decimals: 2, min: 0, max: 1000, width: "half" },
        { code: "posRokUruchomienia", type: "number", label: "Rok uruchomienia oczyszczalni", visibleWhen: PLANT, min: 1900, max: 2100, width: "half" },
        {
          code: "posOdbiornik",
          type: "radio",
          label: "Odbiornik oczyszczonych ścieków",
          required: true,
          visibleWhen: PLANT,
          options: [
            { value: "grunt", label: "grunt" },
            { value: "row", label: "rów melioracyjny" },
            { value: "inny", label: "inny" },
          ],
        },
        {
          code: "posOdbiornikInny",
          type: "text",
          label: "Odbiornik ścieków (inny)",
          required: true,
          visibleWhen: { all: [PLANT, { field: "posOdbiornik", equals: "inny" }] },
          maxLength: 200,
        },
      ],
    },
    {
      code: "wywoz",
      title: "Opróżnianie zbiornika lub osadnika",
      visibleWhen: REGISTER,
      fields: [
        {
          code: "umowaWywoz",
          type: "radio",
          label: "Czy jest podpisana umowa z przedsiębiorcą na opróżnianie zbiornika (osadnika oczyszczalni) i wywóz nieczystości?",
          required: true,
          options: YES_NO,
        },
        { code: "umowaData", type: "date", label: "Data zawarcia umowy", visibleWhen: EMPTYING_CONTRACT, width: "half" },
        { code: "umowaNumer", type: "text", label: "Numer umowy", visibleWhen: EMPTYING_CONTRACT, width: "half", maxLength: 100 },
        {
          code: "przedsiebiorcaWywoz",
          type: "text",
          label: "Nazwa przedsiębiorcy świadczącego usługę wywozu nieczystości ciekłych",
          required: true,
          visibleWhen: EMPTYING_CONTRACT,
          maxLength: 300,
        },
        {
          code: "czestotliwoscWywozu",
          type: "text",
          label: "Częstotliwość opróżniania (np. m³ na tydzień, miesiąc lub rok)",
          visibleWhen: TANK,
          maxLength: 100,
          width: "half",
        },
        { code: "dataOstatniegoWywozu", type: "date", label: "Data ostatniego wywozu", width: "half" },
        { code: "iloscOsadu", type: "text", label: "Ilość wywożonego osadu", visibleWhen: PLANT, maxLength: 100, width: "half" },
        feeInfo(
          "infoUmowa",
          "Właściciel nieruchomości, który pozbywa się nieczystości ciekłych, dokumentuje korzystanie z usługi umową z przedsiębiorcą i dowodami uiszczania opłat — okazuje je na żądanie gminy (art. 6 ust. 1 ustawy o utrzymaniu czystości i porządku w gminach). O zmianie danych zawiadom urząd nowym zgłoszeniem.",
        ),
      ],
    },
    {
      code: "oplata",
      title: "Opłata skarbowa",
      visibleWhen: OPERATION,
      fields: [
        {
          code: "oplataZgloszenia",
          type: "radio",
          label: "Opłata za przyjęcie zgłoszenia",
          required: true,
          options: [
            { value: "oplata", label: "wnoszę opłatę skarbową 120 zł" },
            {
              value: "zwolnienie",
              label:
                "oświadczam, że oczyszczalnia obsługuje wyłącznie budynek mieszkalny — zgłoszenie jest zwolnione z opłaty skarbowej (art. 2 ust. 1 pkt 2 ustawy o opłacie skarbowej)",
            },
          ],
        },
        {
          code: "kwotaOplaty",
          type: "fee",
          label: "Opłata skarbowa do wniesienia",
          feeAmount: 120,
          feeWaivedWhen: FEE_WAIVED,
          hint: "Przelew na rachunek urzędu — dołącz potwierdzenie.",
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    feeProofAttachment("Dowód zapłaty opłaty skarbowej (120 zł)", {
      visibleWhen: { all: [OPERATION, { field: "oplataZgloszenia", equals: "oplata" }] },
    }),
    {
      code: "mapa",
      label: "Mapa sytuacyjno-wysokościowa albo inwentaryzacja powykonawcza z lokalizacją oczyszczalni",
      hint: "Urzędy zwykle o nią proszą (praktyka urzędów).",
      visibleWhen: OPERATION,
    },
    {
      code: "certyfikat",
      label: "Certyfikat lub deklaracja właściwości użytkowych oczyszczalni, dokumentacja techniczna albo instrukcja eksploatacji",
      hint: "Urzędy zwykle o nie proszą (praktyka urzędów).",
      visibleWhen: OPERATION,
    },
    {
      code: "kopiaZgloszeniaBudowy",
      label: "Kopia zgłoszenia budowy staroście z informacją o braku sprzeciwu",
      hint: "Nieobowiązkowo (praktyka urzędów).",
      visibleWhen: { field: "zgloszenieBudowy", equals: "tak" },
    },
    {
      code: "umowaWywozPlik",
      label: "Kopia umowy na opróżnianie zbiornika (osadnika) i ostatnie dowody opłat za wywóz",
      hint: "Nieobowiązkowo — urząd może o nie poprosić przy kontroli (praktyka urzędów).",
      visibleWhen: EMPTYING_CONTRACT,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default B2;
